import { useCallback, useMemo, useRef, useState } from "react";
import type {
  AutoplayType,
  EmblaPluginType,
} from "types/vendor/embla-carousel";
import {
  getAutoplayPlugin,
  getWheelGesturesPlugin,
} from "lib/vendor/embla-plugins";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "components/ui/carousel";
import { formatCurrency } from "lib/utils";
import styles from "./monthly-savings.module.css";

const rates = ["0.05%", "3%", "8%"];

const MonthlySavings = ({ monthlySavings }: { monthlySavings: number[] }) => {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const carouselRef = useRef<HTMLDivElement>(null);

  const carouselAutoplay = carouselApi?.plugins().autoplay as
    | AutoplayType
    | undefined;
  const disableAutoPlay = () => carouselAutoplay?.stop();
  const enableAutoPlay = () => carouselAutoplay?.play();

  const getRootNode = useCallback(() => carouselRef.current, []);

  const autoplayPlugin = useMemo(
    () =>
      getAutoplayPlugin({
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        rootNode: getRootNode,
      }),
    [getRootNode],
  );

  const wheelGesturesPlugin = useMemo(() => getWheelGesturesPlugin(), []);

  const carouselPlugins: (EmblaPluginType | undefined)[] = [
    autoplayPlugin,
    wheelGesturesPlugin,
  ].filter(Boolean);

  return (
    <div className={styles.carouselContainer}>
      <Carousel
        ref={carouselRef}
        setApi={setCarouselApi}
        plugins={carouselPlugins as EmblaPluginType[]}
      >
        <CarouselContent>
          {rates.map((rate, index) => (
            <CarouselItem key={index} className={styles.carouselItem}>
              <div className={styles.gridContainer}>
                <div>
                  <img
                    src={`/assets/retirement/result-${index + 1}.svg`}
                    alt="Retirement results"
                    className={styles.image}
                  />
                </div>
                <div className={styles.textContainer}>
                  <p className={styles.text}>
                    At {rate} p.a. returns, you would{" "}
                    <br className={styles.hiddenMdBlock} /> need to set aside
                  </p>

                  <div className={styles.flexContainer}>
                    <p className={styles.amount}>
                      {formatCurrency(monthlySavings[index])}{" "}
                    </p>
                    <p className={styles.monthly}>monthly</p>
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div
          className={styles.carouselControls}
          onFocusCapture={disableAutoPlay}
          onBlurCapture={enableAutoPlay}
        >
          <CarouselPrevious />
          <CarouselNext />
        </div>
      </Carousel>
    </div>
  );
};

export default MonthlySavings;
